import React, { useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const sections = [
  { id: 'overview', label: 'Platform Overview', icon: '🏠' },
  { id: 'job_seeker', label: 'Job Seekers', icon: '👤' },
  { id: 'employer', label: 'Employers', icon: '🏢' },
  { id: 'course_provider', label: 'Course Providers', icon: '🎓' },
  { id: 'recruiter', label: 'Recruiters', icon: '🔍' },
  { id: 'student', label: 'Students', icon: '📚' },
  { id: 'university', label: 'University Managers', icon: '🏛️' },
  { id: 'event_host', label: 'Event Hosts', icon: '📅' },
  { id: 'admin', label: 'Administrators', icon: '⚙️' },
  { id: 'discounts', label: 'Discount Codes', icon: '🏷️' },
  { id: 'payments', label: 'Payments & Billing', icon: '💳' },
  { id: 'configuration', label: 'Configuration Guide', icon: '🔧' },
];

const SECTION_COMPONENTS = {
  overview: () => <OverviewSection />,
  job_seeker: () => <JobSeekerSection />,
  employer: () => <EmployerSection />,
  course_provider: () => <CourseProviderSection />,
  recruiter: () => <RecruiterSection />,
  student: () => <StudentSection />,
  university: () => <UniversitySection />,
  event_host: () => <EventHostSection />,
  admin: () => <AdminSection />,
  discounts: () => <DiscountsSection />,
  payments: () => <PaymentsSection />,
  configuration: () => <ConfigurationSection />,
};

/* Convert rendered HTML into the plain-text format used by the admin-editable manual:
   "## " sub-heading, "- " bullet, "1. " step, "> " tip/note, blank line between blocks. */
function htmlToManualText(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const clean = (t) => t.replace(/\s+/g, ' ').trim();
  const blocks = [];
  let skippedTitle = false;
  const walk = (el) => {
    for (const node of Array.from(el.children)) {
      const tag = node.tagName.toLowerCase();
      if (tag === 'h2' && !skippedTitle) { skippedTitle = true; continue; }
      if (tag === 'h2' || tag === 'h3') { blocks.push(`## ${clean(node.textContent)}`); continue; }
      if (tag === 'p') { blocks.push(clean(node.textContent)); continue; }
      if (tag === 'ol') {
        blocks.push(Array.from(node.children).map((li, i) => {
          const spans = li.querySelectorAll('span');
          const txt = spans.length > 1 ? spans[spans.length - 1].textContent : li.textContent;
          return `${i + 1}. ${clean(txt)}`;
        }).join('\n'));
        continue;
      }
      if (tag === 'ul') {
        blocks.push(Array.from(node.children).map(li => `- ${clean(li.textContent)}`).join('\n'));
        continue;
      }
      if (tag === 'table') {
        const rows = Array.from(node.querySelectorAll('tbody tr')).map(tr =>
          '- ' + Array.from(tr.children).map(td => clean(td.textContent)).filter(Boolean).join(' | '));
        if (rows.length) blocks.push(rows.join('\n'));
        continue;
      }
      if (/bg-(blue|amber)-50/.test(node.className || '')) { blocks.push(`> ${clean(node.textContent)}`); continue; }
      if (node.children.length === 0) { const t = clean(node.textContent); if (t) blocks.push(t); continue; }
      walk(node);
    }
  };
  walk(doc.body.firstChild);
  return blocks.filter(Boolean).join('\n\n');
}

/* The built-in manual as editable data - used by the admin editor as its starting point. */
export function getDefaultManual() {
  return {
    title: 'User Manual',
    last_updated: '',
    sections: sections.map(s => ({
      heading: s.label,
      body: htmlToManualText(renderToStaticMarkup(SECTION_COMPONENTS[s.id]())),
    })),
  };
}

function ManualBody({ body }) {
  const blocks = (body || '').split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  return (
    <div>
      {blocks.map((b, i) => {
        const lines = b.split('\n');
        if (b.startsWith('## ')) return <H2 key={i}>{b.slice(3)}</H2>;
        if (b.startsWith('> ')) return <Tip key={i}>{b.slice(2)}</Tip>;
        if (lines.every(l => /^\d+\.\s/.test(l))) return <Steps key={i} items={lines.map(l => l.replace(/^\d+\.\s/, ''))} />;
        if (lines.every(l => l.startsWith('- '))) {
          return <ul key={i} className="list-disc ml-6 mb-4 space-y-1 text-gray-600">{lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}</ul>;
        }
        return <p key={i} className="text-gray-600 leading-relaxed mb-3 whitespace-pre-line">{b}</p>;
      })}
    </div>
  );
}

export default function UserManualPage() {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [custom, setCustom] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    api.get('/pages/manual')
      .then(res => {
        if (Array.isArray(res.data?.sections) && res.data.sections.length) setCustom(res.data);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navItems = custom
    ? custom.sections.map((s, i) => ({ key: i, label: s.heading, icon: sections.find(d => d.label === s.heading)?.icon }))
    : sections.map(s => ({ key: s.id, label: s.label, icon: s.icon }));
  const current = custom ? activeIndex : activeSection;
  const select = custom ? setActiveIndex : setActiveSection;
  const activeCustom = custom ? custom.sections[Math.min(activeIndex, custom.sections.length - 1)] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003366]">{custom?.title || 'User Manual'}</h1>
          <p className="text-gray-500 mt-2">
            {custom?.last_updated ? `Last updated: ${custom.last_updated}` : 'Complete guide for all user types on the WorksRun platform'}
          </p>
        </div>

        <div className="flex gap-8">
          {/* Sidebar nav */}
          <aside className="w-56 shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border overflow-hidden sticky top-6">
              {navItems.map(s => (
                <button
                  key={s.key}
                  onClick={() => select(s.key)}
                  className={`w-full text-left flex items-center gap-2 px-4 py-3 text-sm border-b last:border-0 transition-colors ${
                    current === s.key
                      ? 'bg-[#003366] text-white font-semibold'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {s.icon && <span>{s.icon}</span>}
                  <span>{s.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 bg-white rounded-xl shadow-sm border p-8 min-h-[600px]">
            {custom ? (
              activeCustom && (
                <div>
                  <H1>{activeCustom.heading}</H1>
                  <ManualBody body={activeCustom.body} />
                </div>
              )
            ) : SECTION_COMPONENTS[activeSection]()}
          </main>
        </div>
      </div>
    </div>
  );
}

/* ─── Shared components ─── */
function H1({ children }) {
  return <h2 className="text-2xl font-bold text-[#003366] mb-4 pb-2 border-b">{children}</h2>;
}
function H2({ children }) {
  return <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">{children}</h3>;
}
function P({ children }) {
  return <p className="text-gray-600 leading-relaxed mb-3">{children}</p>;
}
function Steps({ items }) {
  return (
    <ol className="space-y-2 ml-2 mb-4">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#003366] text-white text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
          <span className="text-gray-600">{item}</span>
        </li>
      ))}
    </ol>
  );
}
function Tip({ children }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4 text-sm text-blue-800">
      💡 <strong>Tip:</strong> {children}
    </div>
  );
}
function Warning({ children }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 text-sm text-amber-800">
      ⚠️ <strong>Note:</strong> {children}
    </div>
  );
}

/* ─── Sections ─── */
function OverviewSection() {
  return (
    <div>
      <H1>Platform Overview</H1>
      <P>
        WorksRun is an integrated UK employment and education platform connecting job seekers, employers,
        course providers, universities, recruiters, students, and event hosts in one place.
      </P>
      <H2>Who is the platform for?</H2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
        {[
          { icon: '👤', role: 'Job Seekers', desc: 'Find jobs, build CVs, set up alerts, take skills tests' },
          { icon: '🏢', role: 'Employers', desc: 'Post jobs, review applications, manage company profile' },
          { icon: '🎓', role: 'Course Providers', desc: 'Publish courses, track enrolments, grow revenue' },
          { icon: '🔍', role: 'Recruiters', desc: 'Submit candidates, earn commission, manage placements' },
          { icon: '📚', role: 'Students', desc: 'Apply for university, browse student services' },
          { icon: '🏛️', role: 'Universities', desc: 'Manage enrolments, publish programmes, partner with employers' },
          { icon: '📅', role: 'Event Hosts', desc: 'Create and promote career events, sell tickets' },
          { icon: '⚙️', role: 'Administrators', desc: 'Full platform control: users, content, payments, branding' },
        ].map(({ icon, role, desc }) => (
          <div key={role} className="flex gap-3 p-3 border rounded-lg">
            <span className="text-2xl">{icon}</span>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{role}</p>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <H2>Getting Started</H2>
      <Steps items={[
        'Go to the Register page and choose your account type.',
        'Verify your email address using the link sent to your inbox.',
        'Complete your profile to unlock all platform features.',
        'Choose a subscription plan from the Pricing page if required.',
        'Use the navigation menu to access your dashboard.',
      ]} />
      <Tip>Free accounts are available for job seekers. Employers, course providers and other business accounts may require a paid plan.</Tip>
    </div>
  );
}

function JobSeekerSection() {
  return (
    <div>
      <H1>Job Seekers</H1>
      <P>As a job seeker you can search for jobs, track your applications, build a professional CV, set up automated job alerts, take skills tests and message employers directly.</P>

      <H2>Finding Jobs</H2>
      <Steps items={[
        'Click Jobs in the top navigation.',
        'Use the search bar to enter a job title, keyword or company.',
        'Filter by location, salary, contract type or industry.',
        'Click a job card to see the full description.',
        'Click Apply Now and complete the application form.',
      ]} />

      <H2>Setting Up Job Alerts</H2>
      <P>Automated alerts email you when matching jobs are posted.</P>
      <Steps items={[
        'Log in and go to your Dashboard → Job Alerts.',
        'Click + New Alert.',
        'Enter your keywords, location, and preferred frequency (instant, daily or weekly).',
        'Save the alert — you will receive emails automatically.',
      ]} />
      <Tip>Set alerts to "Instant" for urgent job searches so you are notified the moment a match is posted.</Tip>

      <H2>Building Your CV</H2>
      <Steps items={[
        'From your Dashboard, click CV Builder.',
        'Fill in each section: personal info, work history, education, skills.',
        'Preview your CV at any time using the Preview button.',
        'Download as PDF or share a public link with employers.',
      ]} />

      <H2>Taking Skills Tests</H2>
      <Steps items={[
        'Go to Skills Tests in the navigation.',
        'Browse available tests by category (e.g. Python, Marketing, Communication).',
        'Click Start Test — each test is timed, usually 15–30 minutes.',
        'Your score and badge are shown on your profile automatically after completion.',
      ]} />

      <H2>Saving Jobs</H2>
      <P>Click the bookmark icon on any job card to save it. Access saved jobs from your Dashboard → Saved Jobs.</P>

      <H2>Messaging Employers</H2>
      <P>Once you have applied for a job, the employer may open a conversation. Go to Messages in your dashboard to reply.</P>
    </div>
  );
}

function EmployerSection() {
  return (
    <div>
      <H1>Employers</H1>
      <P>Employers can post jobs, review and manage applications, message candidates, and build a company profile to attract talent.</P>

      <H2>Posting a Job</H2>
      <Steps items={[
        'Log in and go to your Employer Dashboard.',
        'Click Post a New Job.',
        'Fill in the job title, description, salary range, location and requirements.',
        'Set the application deadline and contract type.',
        'Click Publish — the job goes live immediately (subject to admin moderation).',
      ]} />
      <Warning>Jobs are reviewed by admins before appearing in search results. This typically takes less than 24 hours.</Warning>

      <H2>Managing Applications</H2>
      <Steps items={[
        'From your Dashboard, click on a live job to see its applicants.',
        'Review each applicant\'s CV, cover letter and skills test scores.',
        'Use the status buttons to mark applicants as Shortlisted, Interviewing, Offered or Rejected.',
        'Message candidates directly from the applicant card.',
      ]} />

      <H2>Company Profile</H2>
      <P>A complete company profile increases application rates. Go to Dashboard → Company Profile to add your logo, company description, website and social links.</P>

      <H2>Using Discount Codes</H2>
      <P>If you have a discount code for a subscription plan, enter it at checkout on the <Link to="/pricing" className="text-[#003366] underline">Pricing page</Link>.</P>
    </div>
  );
}

function CourseProviderSection() {
  return (
    <div>
      <H1>Course Providers</H1>
      <P>Course providers can publish training courses, manage enrolments, and earn revenue directly through the platform.</P>

      <H2>Publishing a Course</H2>
      <Steps items={[
        'Log in and go to your Course Provider Dashboard.',
        'Click + Add New Course.',
        'Enter course title, description, category, price and duration.',
        'Upload a cover image and add curriculum modules.',
        'Click Publish — your course will appear in the Courses section.',
      ]} />

      <H2>Managing Enrolments</H2>
      <Steps items={[
        'From your Dashboard, go to Enrolments.',
        'See all enrolled students with their enrolment date and payment status.',
        'Download the enrolment list as a CSV if needed.',
      ]} />

      <H2>Revenue</H2>
      <P>Payments for course enrolments are collected through the platform. Your earnings are visible in your Dashboard → Revenue section. Payouts are processed according to the platform\'s payment schedule.</P>

      <Tip>Free courses can be used to build your audience and upsell to paid programmes.</Tip>
    </div>
  );
}

function RecruiterSection() {
  return (
    <div>
      <H1>Recruiters</H1>
      <P>Recruiters on WorksRun can submit candidates to employers, track placement commissions, and manage client relationships.</P>

      <H2>Submitting a Candidate</H2>
      <Steps items={[
        'Log in and go to your Recruiter Dashboard.',
        'Click New Submission.',
        'Enter the candidate\'s details and the target role/employer.',
        'Upload the candidate\'s CV.',
        'Submit — the employer will be notified.',
      ]} />

      <H2>Tracking Placements & Commission</H2>
      <P>All active submissions and their statuses (submitted, interviewing, placed, rejected) are shown on your dashboard. Commission earned from successful placements is tracked in the Revenue section.</P>

      <H2>Client Management</H2>
      <P>Use the Clients tab to manage your employer relationships, track active briefs, and record notes on each account.</P>
    </div>
  );
}

function StudentSection() {
  return (
    <div>
      <H1>Students</H1>
      <P>Students can browse universities, apply for degree programmes, access student services (visa support, accommodation) and find part-time jobs.</P>

      <H2>Finding a University</H2>
      <Steps items={[
        'Go to Universities in the top navigation.',
        'Filter by subject area, location or entry requirements.',
        'Click a university card to see available programmes.',
        'Click Apply to submit an application.',
      ]} />

      <H2>Student Services</H2>
      <Steps items={[
        'Go to Student Services in the navigation.',
        'Browse services: visa assistance, accommodation, scholarship guidance.',
        'Fill in the enquiry form for the service you need.',
        'A specialist will contact you within 2 working days.',
      ]} />

      <H2>Part-Time Jobs</H2>
      <P>Use the Jobs section with the filter "Part-time" or "Student-friendly" to find work that fits around your studies.</P>

      <Tip>Some universities have exclusive job partnerships — check your university page for employer spotlights.</Tip>
    </div>
  );
}

function UniversitySection() {
  return (
    <div>
      <H1>University Managers</H1>
      <P>University managers can manage their institution's profile, publish programmes, handle student applications and partner with employers for graduate recruitment.</P>

      <H2>Setting Up Your University Profile</H2>
      <Steps items={[
        'Log in and go to your University Dashboard.',
        'Click Edit Profile.',
        'Add your university name, description, location, website and contact details.',
        'Upload your university logo and cover image.',
        'Save — your profile is now visible to students.',
      ]} />

      <H2>Managing Programmes</H2>
      <Steps items={[
        'Go to Dashboard → Programmes.',
        'Click + Add Programme.',
        'Enter the programme title, level (BSc, MSc, PhD, etc.), fees and entry requirements.',
        'Publish the programme so students can find and apply.',
      ]} />

      <H2>Handling Applications</H2>
      <Steps items={[
        'Applications appear in Dashboard → Applications.',
        'Review each applicant\'s details, qualifications and personal statement.',
        'Update the status: Under Review → Offer Made → Accepted / Declined.',
        'Students are notified automatically at each status change.',
      ]} />

      <H2>Employer Partnerships</H2>
      <P>Use the Partnerships tab to connect with employers for graduate recruitment schemes, placement years and careers fairs.</P>
    </div>
  );
}

function EventHostSection() {
  return (
    <div>
      <H1>Event Hosts</H1>
      <P>Event hosts can create career events (fairs, workshops, webinars), sell tickets, and manage attendees through the platform.</P>

      <H2>Creating an Event</H2>
      <Steps items={[
        'Log in and go to your Event Host Dashboard.',
        'Click + New Event.',
        'Enter event name, description, date, time, location (or online link) and capacity.',
        'Set the ticket price (or mark as free).',
        'Upload a banner image and publish.',
      ]} />

      <H2>Managing Attendees</H2>
      <Steps items={[
        'Go to Dashboard → Attendees for a specific event.',
        'See all registrations with contact details and payment status.',
        'Export the attendee list as CSV for check-in management.',
        'Send bulk messages to attendees directly from the platform.',
      ]} />

      <H2>Revenue</H2>
      <P>Ticket revenue is collected through the platform and visible in your Dashboard → Revenue. Payments are processed via Stripe or PayPal depending on your configuration.</P>

      <Tip>Free events have unlimited capacity. For paid events, the platform takes a small processing fee.</Tip>
    </div>
  );
}

function AdminSection() {
  return (
    <div>
      <H1>Administrators</H1>
      <P>Administrators have full control over every aspect of the WorksRun platform.</P>

      <H2>Admin Dashboard Overview</H2>
      <P>Access the admin panel at <strong>/admin</strong>. The sidebar contains tabs for every management area:</P>
      <div className="grid grid-cols-2 gap-2 my-4 text-sm">
        {[
          ['Overview', 'Platform stats and quick actions'],
          ['Users', 'Manage accounts, roles, verification'],
          ['Jobs', 'Review and moderate job listings'],
          ['Courses', 'Manage course listings'],
          ['Moderation', 'Approve/reject flagged content'],
          ['Revenue', 'Income across all streams'],
          ['Subscriptions', 'Plans and pricing tiers'],
          ['Gateways', 'Configure Stripe, PayPal, M-Pesa'],
          ['Discounts', 'Create promotional codes'],
          ['Branding', 'Logo, colours, contact details'],
          ['Blog', 'Create and manage blog posts'],
          ['Email', 'Provider config and alert triggers'],
          ['Skills Tests', 'Create assessment tests'],
          ['CMS', 'Career tips and homepage content'],
        ].map(([tab, desc]) => (
          <div key={tab} className="flex gap-2 p-2 bg-gray-50 rounded border">
            <span className="font-semibold text-[#003366] w-28 shrink-0">{tab}</span>
            <span className="text-gray-500 text-xs">{desc}</span>
          </div>
        ))}
      </div>

      <H2>User Management</H2>
      <Steps items={[
        'Go to Admin → Users.',
        'Search by name, email or user type.',
        'Click on a user to view their profile.',
        'Use the toggle to activate/deactivate accounts.',
        'Use the Role button to change user type (e.g. promote to admin).',
      ]} />

      <H2>Configuring Payment Gateways</H2>
      <Steps items={[
        'Go to Admin → Gateways.',
        'Click Configure on the payment provider (Stripe, PayPal, M-Pesa, etc.).',
        'Enter the API credentials (Client ID / Secret Key).',
        'Toggle the gateway Active.',
        'Save — users can now pay through this method.',
      ]} />

      <H2>Branding</H2>
      <Steps items={[
        'Go to Admin → Branding.',
        'Update the platform name, tagline, and colours.',
        'Upload a new logo using the Upload Logo File button.',
        'Or paste a direct image URL in the Logo URL field.',
        'Click Save Branding.',
      ]} />

      <H2>Blog Management</H2>
      <Steps items={[
        'Go to Admin → Blog.',
        'Click + New Post to write a blog article.',
        'Set the category, tags and status (Draft or Published).',
        'Switch to the Comments tab to approve or remove reader comments.',
      ]} />

      <H2>Discount Codes</H2>
      <P>See the <strong>Discount Codes</strong> section of this manual for full details on creating and managing promo codes.</P>

      <H2>Platform Configuration</H2>
      <P>Email delivery, payment gateways and other integrations require environment variables set on the server. See the <strong>Configuration Guide</strong> section of this manual for a full reference of every required setting.</P>
    </div>
  );
}

function DiscountsSection() {
  return (
    <div>
      <H1>Discount Codes — How to Create &amp; Use</H1>

      <H2>What are Discount Codes?</H2>
      <P>
        Discount codes (also called promo codes) let administrators offer reduced pricing on subscription plans.
        They can give a percentage off (e.g. 25%) or a fixed amount off (e.g. £10) and can be limited
        by number of uses or an expiry date.
      </P>

      <div className="bg-[#003366]/5 border border-[#003366]/20 rounded-xl p-5 my-4">
        <p className="font-bold text-[#003366] mb-3 text-lg">How to Create a Discount Code (Admin)</p>
        <Steps items={[
          'Log in as an Administrator and go to the Admin Panel (/admin).',
          'Click Discounts in the left sidebar.',
          'Click the + New Code button (top right).',
          'Enter a unique Code — e.g. SUMMER25 (letters and numbers only, auto-uppercased).',
          'Choose the Discount Type: Percentage (%) or Fixed Amount (£).',
          'Enter the Discount Value — e.g. 25 for 25% off, or 10 for £10 off.',
          'Optionally set Max Uses — leave blank for unlimited uses.',
          'Optionally set a Valid Until date after which the code stops working.',
          'Click Create Code.',
        ]} />
        <Tip>Code names should be memorable but not guessable — avoid "TEST" or "ADMIN". Use themed codes like "LAUNCH50" or "WELCOME20".</Tip>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 my-4">
        <p className="font-bold text-emerald-800 mb-3 text-lg">How Users Apply a Discount Code</p>
        <Steps items={[
          'Go to the Pricing page (/pricing).',
          'Choose the subscription plan you want to purchase.',
          'Click the Pay button for that plan.',
          'In the checkout — before completing payment — enter your discount code in the Promo Code field.',
          'Click Apply — the discount is shown immediately.',
          'Complete the payment. The discounted total is charged.',
        ]} />
        <Warning>Discount codes apply to the first billing period only unless otherwise stated. They cannot be combined with other promotions.</Warning>
      </div>

      <H2>Managing Existing Codes</H2>
      <P>All created codes are listed in Admin → Discounts. Each row shows:</P>
      <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4 text-sm">
        <li>The code string and discount value</li>
        <li>How many times it has been used</li>
        <li>The expiry date (if set)</li>
        <li>An Active/Inactive status toggle</li>
        <li>A delete button to permanently remove the code</li>
      </ul>
      <Tip>To temporarily pause a code without deleting it, toggle it Inactive. Toggle it Active again when you want to re-enable it.</Tip>

      <H2>Common Use Cases</H2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
        {[
          { title: 'Launch Promotion', example: 'LAUNCH50 — 50% off', desc: 'Drive sign-ups during platform launch with a time-limited heavy discount.' },
          { title: 'Partner Discount', example: 'PARTNER30 — 30% off', desc: 'Share with partner organisations for their members.' },
          { title: 'Seasonal Sale', example: 'SPRING25 — 25% off', desc: 'Run quarterly promotions to boost subscriptions.' },
          { title: 'Loyalty Reward', example: 'LOYAL10 — £10 off', desc: 'Reward long-term users when they renew or upgrade.' },
        ].map(({ title, example, desc }) => (
          <div key={title} className="p-4 border rounded-lg bg-white">
            <p className="font-semibold text-gray-800 text-sm">{title}</p>
            <p className="font-mono text-xs text-[#E11D48] mb-1">{example}</p>
            <p className="text-xs text-gray-500">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsSection() {
  return (
    <div>
      <H1>Payments &amp; Billing</H1>

      <H2>Supported Payment Methods</H2>
      <P>WorksRun supports multiple payment methods configured by administrators:</P>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
        {[
          { name: 'Stripe', icon: '💳', desc: 'Credit/debit cards — Visa, Mastercard, Amex. Secure PCI-DSS compliant checkout.' },
          { name: 'PayPal', icon: '🔵', desc: 'Pay via your PayPal account or PayPal guest checkout using any card.' },
          { name: 'M-Pesa', icon: '📱', desc: 'Mobile money payments for users in Kenya and East Africa.' },
        ].map(({ name, icon, desc }) => (
          <div key={name} className="p-4 border rounded-lg">
            <p className="text-2xl mb-1">{icon}</p>
            <p className="font-semibold text-gray-800">{name}</p>
            <p className="text-xs text-gray-500 mt-1">{desc}</p>
          </div>
        ))}
      </div>

      <H2>How to Subscribe</H2>
      <Steps items={[
        'Go to the Pricing page (/pricing).',
        'Choose your plan (Starter, Professional, Enterprise, etc.).',
        'If you have a discount code, you will enter it at checkout.',
        'Click Pay with Card (Stripe) or Pay with PayPal.',
        'Complete payment in the secure checkout.',
        'Your account is upgraded immediately on successful payment.',
      ]} />

      <H2>Free Plan</H2>
      <P>A free plan is available for job seekers with core features. Upgrade at any time from the Pricing page to unlock premium features like CV downloads, unlimited applications, and priority listing.</P>

      <H2>Viewing Billing History</H2>
      <P>Go to your Dashboard → Billing to see all past payments, invoices and subscription status. Download receipts from the Payments tab.</P>

      <H2>Cancelling a Subscription</H2>
      <P>Contact the support team or use the Cancel Subscription button in your Dashboard → Billing. Your plan remains active until the end of the current billing period.</P>

      <Warning>Refunds are handled on a case-by-case basis. Contact support within 7 days of payment for refund requests.</Warning>

      <H2>For Administrators — Configuring Payment Gateways</H2>
      <Steps items={[
        'Go to Admin → Gateways.',
        'Click Configure on the desired payment provider.',
        'For Stripe: enter your Secret Key and Publishable Key from the Stripe Dashboard.',
        'For PayPal: enter your Client ID and Client Secret from the PayPal Developer Portal.',
        'Toggle the gateway Active and click Save.',
        'Test with a small transaction before going live.',
      ]} />
      <Tip>PayPal supports both sandbox (test) and live mode. Set PAYPAL_MODE=sandbox in your server environment for testing, then switch to live when ready.</Tip>
    </div>
  );
}

function EnvRow({ name, required, example, desc }) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4 align-top">
        <code className="text-xs bg-gray-100 text-[#003366] px-1.5 py-0.5 rounded font-mono">{name}</code>
        {required && <span className="ml-1 text-[10px] text-red-500 font-bold">required</span>}
      </td>
      <td className="py-2 pr-4 align-top text-xs text-gray-500 font-mono">{example}</td>
      <td className="py-2 align-top text-xs text-gray-600">{desc}</td>
    </tr>
  );
}

function ConfigSection({ title, rows }) {
  return (
    <div className="mb-8">
      <h3 className="text-base font-semibold text-gray-800 mb-3 mt-6">{title}</h3>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500">
              <th className="px-3 py-2 font-semibold w-52">Variable</th>
              <th className="px-3 py-2 font-semibold w-48">Example value</th>
              <th className="px-3 py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody className="px-3">
            {rows.map(r => (
              <tr key={r.name} className="border-t">
                <td className="px-3 py-2 align-top">
                  <code className="text-xs bg-gray-100 text-[#003366] px-1.5 py-0.5 rounded font-mono">{r.name}</code>
                  {r.required && <span className="ml-1 text-[10px] text-red-500 font-bold">required</span>}
                </td>
                <td className="px-3 py-2 align-top text-xs text-gray-500 font-mono break-all">{r.example}</td>
                <td className="px-3 py-2 align-top text-xs text-gray-600">{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConfigurationSection() {
  return (
    <div>
      <H1>Configuration Guide</H1>
      <P>
        WorksRun is configured via a <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">.env</code> file
        in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">backend/</code> directory.
        Copy <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">.env.example</code> to{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">.env</code> and fill in the values below.
        Variables marked <span className="text-red-500 font-bold text-xs">required</span> must be set for the platform to function.
      </P>

      <Warning>Never commit your .env file to version control. It contains secret keys. Add it to .gitignore.</Warning>

      <ConfigSection title="Core Settings" rows={[
        { name: 'MONGO_URL', required: true, example: 'mongodb://localhost:27017', desc: 'MongoDB connection string. Use Atlas URI for cloud hosting.' },
        { name: 'DB_NAME', required: true, example: 'worksrun', desc: 'MongoDB database name.' },
        { name: 'SECRET_KEY', required: true, example: 'a-long-random-string', desc: 'JWT signing secret. Generate with: python3 -c "import secrets; print(secrets.token_hex(32))"' },
        { name: 'FRONTEND_URL', required: true, example: 'https://yoursite.com', desc: 'Public URL of the frontend. Used in email links (password reset, verification).' },
        { name: 'ALLOWED_ORIGINS', required: false, example: 'https://yoursite.com,https://worksrun.com', desc: 'Comma-separated list of allowed CORS origins.' },
      ]} />

      <ConfigSection title="Email — SendGrid (recommended)" rows={[
        { name: 'SENDGRID_API_KEY', required: false, example: 'SG.xxxxxxxxxxxx', desc: 'SendGrid API key. If set, SendGrid is used first for all outgoing email.' },
        { name: 'FROM_EMAIL', required: false, example: 'noreply@yoursite.com', desc: 'The "From" email address shown to recipients. Defaults to noreply@worksrun.com.' },
        { name: 'FROM_NAME', required: false, example: 'WorksRun', desc: 'The "From" display name shown to recipients.' },
      ]} />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2 text-sm text-blue-800">
        💡 <strong>How to get a SendGrid API key:</strong> Sign up at sendgrid.com → Settings → API Keys → Create API Key → choose "Mail Send" permission.
      </div>

      <ConfigSection title="Email — Mailgun (fallback)" rows={[
        { name: 'MAILGUN_API_KEY', required: false, example: 'key-xxxxxxxxxxxxxxxx', desc: 'Mailgun private API key. Used if SendGrid is not configured.' },
        { name: 'MAILGUN_DOMAIN', required: false, example: 'mg.yoursite.com', desc: 'Your verified Mailgun sending domain.' },
      ]} />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2 text-sm text-blue-800">
        💡 <strong>How to get Mailgun credentials:</strong> Sign up at mailgun.com → Sending → Domains → Add Domain → verify DNS → copy API key from API Keys section.
      </div>

      <ConfigSection title="Email — SMTP (fallback)" rows={[
        { name: 'SMTP_HOST', required: false, example: 'smtp.gmail.com', desc: 'SMTP server hostname. Used as a last resort if SendGrid and Mailgun are not set.' },
        { name: 'SMTP_PORT', required: false, example: '587', desc: 'SMTP port. 587 for STARTTLS (recommended), 465 for SSL, 25 for plain.' },
        { name: 'SMTP_USER', required: false, example: 'you@gmail.com', desc: 'SMTP login username (usually your email address).' },
        { name: 'SMTP_PASS', required: false, example: 'app-password-here', desc: 'SMTP password. For Gmail, use an App Password (not your Google account password).' },
      ]} />

      <Warning>Configure at least one email provider (SendGrid, Mailgun, or SMTP). Without one, password reset emails, email verification, and job alert emails will not be sent.</Warning>

      <ConfigSection title="Stripe Payments" rows={[
        { name: 'STRIPE_API_KEY', required: false, example: 'sk_test_51xxx...', desc: 'Stripe secret key. Required to accept card payments via Stripe. Get from Stripe Dashboard → Developers → API Keys.' },
      ]} />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2 text-sm text-blue-800">
        💡 <strong>Stripe setup steps:</strong> Sign up at stripe.com → Developers → API Keys → copy the Secret key (starts with <code>sk_test_</code> for test mode, <code>sk_live_</code> for production). After setting the key, go to Admin → Gateways → Stripe → toggle Active.
      </div>

      <ConfigSection title="PayPal Payments" rows={[
        { name: 'PAYPAL_CLIENT_ID', required: false, example: 'AaBbCc123...', desc: 'PayPal REST API Client ID from the PayPal Developer Dashboard.' },
        { name: 'PAYPAL_CLIENT_SECRET', required: false, example: 'EeFfGg456...', desc: 'PayPal REST API Client Secret. Keep this private — never expose it to the frontend.' },
        { name: 'PAYPAL_MODE', required: false, example: 'sandbox', desc: 'Set to "sandbox" for testing or "live" for production. Defaults to "sandbox".' },
      ]} />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2 text-sm text-blue-800">
        <p><strong>💡 PayPal setup steps:</strong></p>
        <ol className="list-decimal list-inside mt-2 space-y-1">
          <li>Go to <strong>developer.paypal.com</strong> and log in.</li>
          <li>Click <strong>Apps & Credentials</strong> → <strong>Create App</strong>.</li>
          <li>Name your app (e.g. "WorksRun") and choose <strong>Merchant</strong> type.</li>
          <li>Copy the <strong>Client ID</strong> and <strong>Secret Key</strong> shown under your app.</li>
          <li>Use <strong>Sandbox</strong> credentials for testing — switch to <strong>Live</strong> credentials for production.</li>
          <li>Set <code>PAYPAL_MODE=live</code> and update keys when going live.</li>
          <li>After setting the keys, go to <strong>Admin → Gateways → PayPal → toggle Active</strong>.</li>
        </ol>
      </div>

      <ConfigSection title="AI Services (optional)" rows={[
        { name: 'ANTHROPIC_API_KEY', required: false, example: 'sk-ant-api03-xxx...', desc: 'Anthropic Claude API key. Powers AI CV scoring, job matching, and cover letter generation. Get from console.anthropic.com.' },
        { name: 'OPENAI_API_KEY', required: false, example: 'sk-xxx...', desc: 'OpenAI API key. Alternative AI provider for the same features.' },
      ]} />

      <ConfigSection title="File Storage (optional)" rows={[
        { name: 'AWS_ACCESS_KEY_ID', required: false, example: 'AKIA...', desc: 'AWS access key for S3 file storage (CV uploads, images). If not set, files are stored locally.' },
        { name: 'AWS_SECRET_ACCESS_KEY', required: false, example: 'wJalrXUtnFEMI...', desc: 'AWS secret key for S3.' },
        { name: 'AWS_S3_BUCKET', required: false, example: 'worksrun-uploads', desc: 'S3 bucket name for file uploads.' },
        { name: 'AWS_REGION', required: false, example: 'eu-west-2', desc: 'AWS region where the S3 bucket is hosted.' },
      ]} />

      <H2>Quick-Start Checklist</H2>
      <div className="space-y-2 my-4">
        {[
          { item: 'MONGO_URL and DB_NAME set', detail: 'Platform will not start without a database connection.' },
          { item: 'SECRET_KEY set to a random string', detail: 'Required for secure JWT authentication.' },
          { item: 'FRONTEND_URL set to your domain', detail: 'Required for correct links in emails.' },
          { item: 'At least one email provider configured', detail: 'SendGrid API key is the easiest option.' },
          { item: 'STRIPE_API_KEY set (for card payments)', detail: 'Then activate Stripe in Admin → Gateways.' },
          { item: 'PAYPAL_CLIENT_ID + SECRET set (for PayPal)', detail: 'Then activate PayPal in Admin → Gateways.' },
        ].map(({ item, detail }, i) => (
          <div key={i} className="flex gap-3 p-3 border rounded-lg bg-gray-50">
            <div className="w-5 h-5 rounded border-2 border-[#003366] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-800">{item}</p>
              <p className="text-xs text-gray-500">{detail}</p>
            </div>
          </div>
        ))}
      </div>

      <Warning>After editing .env, restart the backend server for changes to take effect: stop the process and run <code className="font-mono bg-amber-100 px-1 rounded">python3 server.py</code> again.</Warning>
    </div>
  );
}
